import { Injectable, inject } from '@angular/core';
import { Subject, lastValueFrom, map } from 'rxjs';
import { ProductControllerService } from 'src/app/admin-api';
import { getDiamanteVermelhoLabel } from 'src/app/models';

/**
 * Resolves Diamante Vermelho labels for product variants without flooding
 * the API (subscriber "Exibir produtos" can list hundreds of SKUs).
 */
@Injectable({ providedIn: 'root' })
export class DiamanteVermelhoLookupService {
  private readonly productService = inject(ProductControllerService);
  private readonly cache = new Map<number, string | null>();
  private readonly queued = new Set<number>();
  private readonly queue: number[] = [];
  private active = 0;
  private readonly maxConcurrent = 3;
  private readonly updatedSubject = new Subject<void>();

  readonly updated$ = this.updatedSubject.asObservable();

  labelFor(productVariantId: number | undefined): string | null | undefined {
    if (!productVariantId) {
      return null;
    }
    if (!this.cache.has(productVariantId)) {
      return undefined;
    }
    return this.cache.get(productVariantId) ?? null;
  }

  enqueue(productVariantIds: Array<number | undefined | null>): void {
    for (const id of productVariantIds) {
      if (!id || this.cache.has(id) || this.queued.has(id)) {
        continue;
      }
      this.queued.add(id);
      this.queue.push(id);
    }
    this.pump();
  }

  private pump(): void {
    while (this.active < this.maxConcurrent && this.queue.length > 0) {
      const productVariantId = this.queue.shift();
      if (!productVariantId) {
        continue;
      }
      this.active += 1;
      void this.fetchOne(productVariantId)
        .catch(() => {
          this.cache.set(productVariantId, null);
        })
        .finally(() => {
          this.queued.delete(productVariantId);
          this.active -= 1;
          this.updatedSubject.next();
          this.pump();
        });
    }
  }

  private async fetchOne(productVariantId: number): Promise<void> {
    const label = await lastValueFrom(
      this.productService.findProductVariantById(productVariantId).pipe(
        map((data) => getDiamanteVermelhoLabel(data.result?.diamanteVermelho))
      )
    );
    this.cache.set(productVariantId, label);
  }
}
